import React, {type ReactNode} from 'react';

import cs from 'classnames';
import styled from 'styled-components';

import {SidebarTopIconButton} from 'app/components/general/DetailPage/RightSidebar/structure/StickyButtons';
import {useWidgetProps} from 'app/components/general/DetailPage/RightSidebar/structure/StickyIsOpenGateway';
import {
  StyledHeaderDIV,
  StyledTitleH5,
} from 'app/general/structures/DocSidebarPagePanels';
import {useToggleWidgetIsOpen, useWidgetIsOpenState} from 'app/settings/hooks';
import {WidgetNameLabel} from 'app/settings/slice';
import {gt} from 'app/utils/i18n';

import type {StickyContentPanelType} from 'app/components/general/DetailPage/RightSidebar/structure/types';
import type {DetailPageItemType} from 'app/components/general/DetailPage/types';

export interface StickySidebarContentPanelProps<
  ItemType extends DetailPageItemType
> extends Omit<StickyContentPanelType<ItemType>, 'version'> {
  children: ReactNode;
  widgetName: keyof typeof WidgetNameLabel;
  topActions?: ReactNode;
  bottomActions?: ReactNode;
}

export function StickySidebarContentPanel<ItemType extends DetailPageItemType>({
  children,
  object,
  widgetName,
  isFullscreen,
  setFullscreenState,
  topActions,
  bottomActions,
}: StickySidebarContentPanelProps<ItemType>) {
  return (
    <BaseStickySidebarContentPanel
      object={object}
      widgetName={widgetName}
      isFullscreen={isFullscreen}
      bottomActions={bottomActions}
      topActions={
        <>
          {!isFullscreen && (
            <SidebarTopIconButton
              tooltip={gt('Expand %s', WidgetNameLabel[widgetName])}
              onClick={() => setFullscreenState(!isFullscreen)}
              icon="fa-expand-arrows-alt"
            />
          )}
          {topActions}
        </>
      }
    >
      {children}
    </BaseStickySidebarContentPanel>
  );
}

export type BaseStickySidebarContentPanelProps<
  ItemType extends DetailPageItemType
> = Omit<StickySidebarContentPanelProps<ItemType>, 'setFullscreenState'>;

export function BaseStickySidebarContentPanel<
  ItemType extends DetailPageItemType
>({
  children,
  object,
  widgetName,
  isFullscreen,
  topActions,
  bottomActions,
}: BaseStickySidebarContentPanelProps<ItemType>) {
  const toggleWidgetIsOpen = useToggleWidgetIsOpen();
  const widgetProps = useWidgetProps(object, widgetName);
  const isOpen = useWidgetIsOpenState(widgetProps);

  if (!isOpen) return null;

  return (
    <SidebarDiv
      className={cs('d-flex flex-column justify-content-between w-100', {
        'border-left': !isFullscreen,
        'shadow-sm': !isFullscreen,
        fullscreen: isFullscreen,
      })}
    >
      <StyledHeaderDIV className="d-flex align-items-center justify-content-between">
        <StyledTitleH5 className="text-truncate">
          {gt(WidgetNameLabel[widgetName])}
        </StyledTitleH5>

        <div className="btn-group title-buttons d-print-none">
          {topActions}
          <SidebarTopIconButton
            tooltip={gt('Hide %s', WidgetNameLabel[widgetName])}
            onClick={() => toggleWidgetIsOpen(widgetProps)}
            icon="fa-chevron-right"
          />
        </div>
      </StyledHeaderDIV>

      <div className="d-flex flex-column flex-grow-1 overflow-auto position-relative">
        {children}
      </div>

      <div className="bg-white">{bottomActions}</div>
    </SidebarDiv>
  );
}

const SidebarDiv = styled.div`
  /* default (collapsed) style */
  max-width: 250px;
  /* common layout */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  &.fullscreen {
    position: absolute;
    right: 0;
    width: 100vw;
    max-width: none;
  }
`;
